import React, { useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { SITE_CONFIG } from '../config';
import { SEO_CONFIG } from '../config/seo';
import { SEO_PAGES } from '../config/seoPages';
import { useTheme } from '../contexts/ThemeContext';
import './SEOPage.css';

const SEOPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const page = SEO_PAGES.find(p => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    if (isDarkMode) document.body.classList.add('dark-mode');
    else document.body.classList.remove('dark-mode');
    return () => { document.body.style.overflow = ''; document.body.style.position = ''; };
  }, [isDarkMode, slug]);

  if (!page) {
    return (
      <div className="maricopa-guide-page">
        <main className="maricopa-guide-not-found">
          <button onClick={() => navigate('/')}>← Return home</button>
          <h1>Page not found</h1>
        </main>
      </div>
    );
  }

  const relatedPages = page.relatedSlugs?.length
    ? page.relatedSlugs.map(relatedSlug => SEO_PAGES.find(p => p.slug === relatedSlug)).filter(Boolean)
    : SEO_PAGES.filter(p => p.slug !== slug).slice(0, 4);
  const faqSchema = page.faqs?.length ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: page.faqs.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer }
    }))
  } : null;

  return (
    <div className="maricopa-guide-page">
      {faqSchema && <Helmet><script type="application/ld+json">{JSON.stringify(faqSchema)}</script></Helmet>}
      <nav className="maricopa-guide-nav" aria-label="Guide navigation">
        <Link to="/">← Sandy Hopkins</Link>
        <a href={`tel:${SEO_CONFIG.AGENT_PHONE}`}>Call {SITE_CONFIG.AGENT.PHONE}</a>
      </nav>

      <main className="maricopa-guide-shell">
        <header className="maricopa-guide-hero">
          <span className="maricopa-guide-eyebrow">The Maricopa Field Guide</span>
          <h1>{page.h1}</h1>
          <p>{page.summary || page.description}</p>
          <div className="maricopa-guide-byline">
            <span>Local guidance by {SEO_CONFIG.AGENT_NAME}</span>
            {page.updated && <span>Reviewed {page.updated}</span>}
          </div>
        </header>

        <article className="maricopa-guide-article">
          {page.content.map((section, i) => (
            <section key={section.heading} className="maricopa-guide-section">
              <span aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
              <div>
                <h2>{section.heading}</h2>
                <p>{section.text}</p>
              </div>
            </section>
          ))}
        </article>

        {page.faqs?.length > 0 && (
          <section className="maricopa-guide-faq" aria-labelledby="maricopa-guide-faq-title">
            <span>Questions buyers and sellers ask</span>
            <h2 id="maricopa-guide-faq-title">Maricopa FAQs</h2>
            <div>
              {page.faqs.map(({ question, answer }) => (
                <details key={question}>
                  <summary>{question}</summary>
                  <p>{answer}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        {page.sources?.length > 0 && (
          <aside className="maricopa-guide-sources" aria-label="Official local sources">
            <strong>Official local resources</strong>
            <div>
              {page.sources.map(({ label, url }) => (
                <a key={url} href={url} target="_blank" rel="noopener noreferrer">{label}</a>
              ))}
            </div>
            <p>Community information can change. Verify details that matter to your move with the appropriate city, county, school, HOA, or state agency.</p>
          </aside>
        )}

        <section className="maricopa-guide-cta">
          <span>Make your next move with a local expert</span>
          <h2>Have a Maricopa question?</h2>
          <p>Tell Sandy what you are considering. Get thoughtful, property-specific guidance without pressure.</p>
          <div>
            <a href={`mailto:${SEO_CONFIG.AGENT_EMAIL}?subject=${encodeURIComponent(page.h1)}`}>Email Sandy</a>
            <a href={`tel:${SEO_CONFIG.AGENT_PHONE}`}>Call {SITE_CONFIG.AGENT.PHONE}</a>
          </div>
        </section>

        <nav className="maricopa-guide-related" aria-label="Related Maricopa guides">
          <h3>Related Maricopa Guides</h3>
          <div>
            {relatedPages.map(p => (
              <Link key={p.slug} to={`/guide/${p.slug}`}>
                <span>{p.title.split('|')[0].trim()}</span><b aria-hidden="true">↗</b>
              </Link>
            ))}
          </div>
        </nav>
      </main>
    </div>
  );
};

export default SEOPage;
